// ============================================================
// TALENTS THAT APPLY AGAINST A KIND OF TARGET - judged, not switched.
//
// Hatred (Orks), Hunter of Aliens and Scourge of Heretics each hold only
// against some targets. They used to be two manual switches on Controls
// (Hatred, Re-roll Damage) that somebody had to remember. Each is now a
// condition (`effectWhen`) judged against the current target, and the
// switch turns itself on while it holds. The manual switches stay: the
// table can always overrule.
//
// WHAT MATCHES (user ruling, 2026-10-05):
//   - Hatred (X): the target carries the tag X - singular or plural, any
//     case ("Orks" matches the tag "ork").
//   - Hunter of Aliens: the target's origin class is Xenos.
//   - Scourge of Heretics: the target is tagged "heretic" or "witch".
//
// Unknown is not yes: with nobody targeted nothing is switched on.
//
// Pure. Zero React dependency.
// ============================================================
import { getEffectiveTalents } from './effectiveTalents';
import { holds } from './effectWhen';
import type { Situation, When } from './effectWhen';
import type { Character } from './types';

/** What a talent switches on for the attack, and the reason to show; null when it does not hold. */
export interface TalentRulings {
  /** Why Hatred holds against this target: "Hatred (Orks)". */
  hatred: string | null;
  /** Why damage may be re-rolled against this target: "Hunter of Aliens". */
  rerollDamage: string | null;
}

type Ruling = keyof TalentRulings;

interface Rule {
  ruling: Ruling;
  /** The condition, given the talent's parameter; null when it cannot be told (Hatred of nothing). */
  when: (parameter: string | undefined) => When | null;
  /** How the reason reads. */
  label: (parameter: string | undefined) => string;
}

/** One row per talent that holds against a kind of target. Adding one is a row. */
const RULES: Record<string, Rule> = {
  hatred: {
    ruling: 'hatred',
    when: (parameter) => (parameter?.trim() ? [[{ kind: 'target_tag', is: parameter.trim() }]] : null),
    label: (parameter) => `Hatred (${parameter?.trim()})`,
  },
  hunter_of_aliens: {
    ruling: 'rerollDamage',
    when: () => [[{ kind: 'target_origin', is: 'XENOS' }]],
    label: () => 'Hunter of Aliens',
  },
  scourge_of_heretics: {
    ruling: 'rerollDamage',
    when: () => [[{ kind: 'target_tag', is: 'heretic' }], [{ kind: 'target_tag', is: 'witch' }]],
    label: () => 'Scourge of Heretics',
  },
};

/** What this character's talents switch on against the current target. */
export function talentRulings(
  character: Pick<Character, 'talents' | 'traits'>,
  situation: Pick<Situation, 'target'>,
): TalentRulings {
  const out: TalentRulings = { hatred: null, rerollDamage: null };
  for (const inst of getEffectiveTalents(character as Character)) {
    const rule = RULES[inst.talentId];
    const when = rule?.when(inst.parameter);
    if (rule && when && holds(when, situation) === true) out[rule.ruling] ??= rule.label(inst.parameter);
  }
  return out;
}
